import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Browser-native speech recognition, mirrored from apps/web/src/lib/
 * useDictation.ts (same shape, same Web Speech API glue: no API key, no audio
 * upload). Kept as its own local copy rather than a cross-app import (this app
 * never imports apps/web directly, same reasoning packages/voice-client's own
 * header gives for why it exists as a package instead) — the hook itself has
 * no corpus-specific logic to drift, so a straight copy is the right call
 * here rather than promoting it to a shared package for one caller.
 *
 * ONE deliberate change from the apps/web original: no `lang: 'en-AU'`
 * default. Per this project's locale rule (CLAUDE.md: "Conventions... derive
 * from the target role's country; en-US is Jason's default — never hard-code
 * AU"), the caller may pass an explicit `lang`; omitting it lets the browser
 * use its own default locale rather than this app asserting AU on every user.
 */

interface SpeechRecognitionResultLike {
  isFinal: boolean;
  0: { transcript: string; confidence: number };
}

interface SpeechRecognitionEventLike extends Event {
  results: ArrayLike<SpeechRecognitionResultLike>;
  resultIndex: number;
}

interface SpeechRecognitionLike extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: Event) => void) | null;
  onend: (() => void) | null;
}

type RecognitionConstructor = new () => SpeechRecognitionLike;

function getRecognitionCtor(): RecognitionConstructor | null {
  const w = globalThis as unknown as Record<string, unknown>;
  const ctor = (w.SpeechRecognition ?? w.webkitSpeechRecognition) as
    | RecognitionConstructor
    | undefined;
  return ctor ?? null;
}

export type DictationState = 'unsupported' | 'idle' | 'recording' | 'error';

export function useDictation(opts?: {
  lang?: string;
  onFinalChunk?: (text: string) => void;
}) {
  const ctor = getRecognitionCtor();
  const [state, setState] = useState<DictationState>(ctor ? 'idle' : 'unsupported');
  const [interim, setInterim] = useState('');
  const recRef = useRef<SpeechRecognitionLike | null>(null);

  useEffect(() => () => recRef.current?.abort(), []);

  const start = useCallback(() => {
    if (!ctor) return;
    const rec = new ctor();
    rec.continuous = true;
    rec.interimResults = true;
    // No AU default (see module header) — omit `lang` entirely when the
    // caller supplies none, so the browser's own locale applies.
    if (opts?.lang) rec.lang = opts.lang;

    rec.onresult = (event) => {
      let interimText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0].transcript;
        if (result.isFinal) {
          opts?.onFinalChunk?.(transcript);
        } else {
          interimText += transcript;
        }
      }
      setInterim(interimText);
    };
    rec.onerror = () => setState('error');
    rec.onend = () => {
      setState((s) => (s === 'recording' ? 'idle' : s));
      setInterim('');
    };

    recRef.current = rec;
    rec.start();
    setState('recording');
  }, [ctor, opts]);

  const stop = useCallback(() => {
    recRef.current?.stop();
    recRef.current = null;
    setInterim('');
  }, []);

  return { state, interim, start, stop, supported: state !== 'unsupported' };
}
